/**
 * rb-templates.js — the template registry (pure render-config data).
 *
 * Pure leaf module. Each template is DATA, not code: a set of layout +
 * style knobs that rb-render.js interprets. Adding a template = adding a row.
 * Switching template never touches the ResumeModel, so user content is
 * preserved across template changes (the "change template, content stays"
 * guarantee).
 *
 * Knob vocabulary (consumed by rb-render.js):
 *   layout       single | header-band | sidebar-left | sidebar-right
 *                | two-column | tabular | grid
 *   header       centered | left | split | band | photo-block | compact
 *   sectionStyle underline | plain | rule | caps | boxed
 *   font         sans | serif | mono | grotesk
 *   accentMode   minimal | headings | full
 *   density      airy | normal | compact
 *   skillStyle   bar | chip | text
 *   photoShape   rect | round
 *
 * globalThis.RbTemplates
 */
(function () {
    'use strict';

    function tpl(id, category, layout, header, sectionStyle, font, accentMode, density, skillStyle, opts) {
        opts = opts || {};
        return {
            id: id,
            nameKey: 'tpl_' + id.replace(/-/g, '_'),
            category: category,
            categoryKey: 'cat_' + category,
            layout: layout,
            header: header,
            sectionStyle: sectionStyle,
            font: font,
            accentMode: accentMode,
            density: density,
            skillStyle: skillStyle,
            photoShape: opts.photoShape || 'rect',
            regions: opts.regions || [],
            atsSafe: opts.atsSafe !== false ? (layout === 'single' || layout === 'two-column') && opts.atsSafe !== false : false,
            signature: !!opts.signature,
            extras: opts.extras || []
        };
    }
    /* atsSafe override: single/two-column default true unless explicitly false;
       multi-column/tabular default false unless explicitly true. */
    function fix(t, atsSafe) { t.atsSafe = !!atsSafe; return t; }

    var LIST = [
        /* ---- Professional (8) ---- */
        fix(tpl('classic',     'professional', 'single',       'centered', 'underline', 'sans',    'headings', 'normal',  'bar'),  true),
        fix(tpl('executive',   'professional', 'header-band',  'band',     'underline', 'sans',    'full',     'normal',  'bar'),  false),
        fix(tpl('corporate',   'professional', 'single',       'left',     'rule',      'sans',    'headings', 'normal',  'chip'), true),
        fix(tpl('swiss',       'professional', 'single',       'split',    'plain',     'grotesk', 'minimal',  'airy',    'text'), true),
        fix(tpl('minimal',     'professional', 'single',       'left',     'plain',     'sans',    'minimal',  'airy',    'text'), true),
        fix(tpl('traditional', 'professional', 'single',       'centered', 'rule',      'serif',   'minimal',  'normal',  'text'), true),
        fix(tpl('polished',    'professional', 'header-band',  'band',     'underline', 'serif',   'full',     'normal',  'bar'),  false),
        fix(tpl('formal',      'professional', 'single',       'centered', 'caps',      'serif',   'minimal',  'normal',  'text'), true),

        /* ---- Modern (8) ---- */
        fix(tpl('clean',          'modern', 'single',        'left',        'rule',      'sans',    'headings', 'normal',  'chip'), true),
        fix(tpl('creative',       'modern', 'sidebar-left',  'photo-block', 'caps',      'sans',    'full',     'normal',  'bar', { photoShape: 'round' }), false),
        fix(tpl('elegant',        'modern', 'single',        'centered',    'plain',     'serif',   'minimal',  'airy',    'text'), true),
        fix(tpl('compact',        'modern', 'single',        'compact',     'underline', 'sans',    'headings', 'compact', 'bar'),  true),
        fix(tpl('twotone',        'modern', 'sidebar-right', 'split',       'boxed',     'sans',    'full',     'normal',  'chip'), false),
        fix(tpl('sidebar-modern', 'modern', 'sidebar-left',  'left',        'plain',     'grotesk', 'headings', 'normal',  'bar'),  false),
        fix(tpl('gradient',       'modern', 'header-band',   'band',        'underline', 'grotesk', 'full',     'normal',  'chip'), false),
        fix(tpl('contemporary',   'modern', 'two-column',    'left',        'rule',      'sans',    'headings', 'normal',  'chip'), true),

        /* ---- Simple / ATS-first (4) ---- */
        fix(tpl('ats-pro',      'ats', 'single', 'left',     'caps',  'sans', 'minimal',  'normal', 'text'), true),
        fix(tpl('plaintext',    'ats', 'single', 'left',     'plain', 'sans', 'minimal',  'normal', 'text'), true),
        fix(tpl('starter',      'ats', 'single', 'centered', 'underline', 'sans', 'headings', 'airy', 'text'), true),
        fix(tpl('single-basic', 'ats', 'single', 'left',     'rule',  'sans', 'headings', 'normal', 'text'), true),

        /* ---- Academic (4) ---- */
        fix(tpl('research',  'academic', 'single', 'left',     'underline', 'serif', 'headings', 'normal', 'text'), true),
        fix(tpl('professor', 'academic', 'single', 'centered', 'rule',      'serif', 'minimal',  'airy',   'text'), true),
        fix(tpl('student',   'academic', 'single', 'centered', 'underline', 'sans',  'headings', 'normal', 'bar'),  true),
        fix(tpl('scholar',   'academic', 'single', 'left',     'caps',      'serif', 'minimal',  'normal', 'text'), true),

        /* ---- Technical (4) ---- */
        fix(tpl('developer',     'technical', 'single',       'compact', 'boxed', 'mono',    'full',     'normal',  'chip'), true),
        fix(tpl('engineer',      'technical', 'two-column',   'left',    'rule',  'sans',    'headings', 'compact', 'bar'),  true),
        fix(tpl('it-specialist', 'technical', 'sidebar-left', 'left',    'plain', 'mono',    'headings', 'normal',  'chip'), false),
        fix(tpl('datapro',       'technical', 'single',       'left',    'rule',  'grotesk', 'headings', 'normal',  'bar'),  true),

        /* ---- Regional (4) ---- */
        fix(tpl('lebenslauf',    'regional', 'tabular',      'photo-block', 'plain', 'serif', 'minimal',  'normal',  'text', { regions: ['de'], signature: true }), false),
        fix(tpl('korean-resume', 'regional', 'tabular',      'photo-block', 'rule',  'sans',  'minimal',  'normal',  'text', { regions: ['ko'] }), false),
        fix(tpl('rirekisho',     'regional', 'grid',         'photo-block', 'plain', 'sans',  'minimal',  'compact', 'text', { regions: ['ja'] }), false),
        fix(tpl('europass',      'regional', 'sidebar-left', 'left',        'rule',  'sans',  'headings', 'normal',  'bar',  { regions: ['fr', 'es', 'de', 'pt'] }), false)
    ];

    var BY_ID = {};
    LIST.forEach(function (t) { BY_ID[t.id] = t; });

    var CATEGORIES = ['professional', 'modern', 'ats', 'academic', 'technical', 'regional'];

    function get(id) {
        return BY_ID[id] || BY_ID.classic;
    }

    globalThis.RbTemplates = {
        LIST: LIST,
        BY_ID: BY_ID,
        CATEGORIES: CATEGORIES,
        get: get,
        ids: function () { return LIST.map(function (t) { return t.id; }); }
    };
})();
